import { useCallback, useId, useRef, useState } from 'react';
import type { Media } from '../lib/types';
import { VideoPlayer } from './VideoPlayer';
import './MediaCarousel.css';

interface Props {
  /** Ordered media items (images + videos) rendered in sequence. */
  items: Media[];
  /** Fallback aspect ratio for items without their own `aspect_ratio`. */
  aspect?: number;
  /** Fires when an image slide is clicked, with that item's index. */
  onOpenLightbox?: (index: number) => void;
  /** Pager style under the viewport: 'dots' (default) or a 'thumbs' filmstrip
   *  (Saleroom lot gallery). */
  variant?: 'dots' | 'thumbs';
}

const SWIPE_THRESHOLD = 48; // px of horizontal travel before a swipe pages

export function MediaCarousel({ items, aspect = 16 / 9, onOpenLightbox, variant = 'dots' }: Props) {
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const baseId = useId();
  // Pointer-drag bookkeeping for swipe paging.
  const dragStartX = useRef<number | null>(null);
  const dragDelta = useRef(0);

  const count = items.length;
  const clamp = useCallback(
    (i: number) => Math.max(0, Math.min(count - 1, i)),
    [count],
  );
  const goTo = useCallback((i: number) => setIndex((cur) => clamp(i ?? cur)), [clamp]);
  const prev = useCallback(() => setIndex((i) => clamp(i - 1)), [clamp]);
  const next = useCallback(() => setIndex((i) => clamp(i + 1)), [clamp]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
  }

  function onPointerDown(e: React.PointerEvent) {
    dragStartX.current = e.clientX;
    dragDelta.current = 0;
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragStartX.current === null) return;
    dragDelta.current = e.clientX - dragStartX.current;
  }
  function onPointerEnd() {
    if (dragStartX.current === null) return;
    const delta = dragDelta.current;
    dragStartX.current = null;
    dragDelta.current = 0;
    if (delta <= -SWIPE_THRESHOLD) next();
    else if (delta >= SWIPE_THRESHOLD) prev();
  }

  if (count === 0) return null;

  // Single item — render cleanly with no controls.
  if (count === 1) {
    return (
      <div className="carousel carousel--single">
        <div className="carousel__viewport">
          <div className="carousel__slide">
            <Slide item={items[0]} index={0} aspect={aspect} onOpenLightbox={onOpenLightbox} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label="Media gallery"
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      <div
        className="carousel__viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onPointerLeave={onPointerEnd}
      >
        <div
          ref={trackRef}
          className="carousel__track"
          style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
        >
          {items.map((item, i) => (
            <div
              key={item.id}
              className="carousel__slide"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
              aria-hidden={i !== index}
              id={`${baseId}-slide-${i}`}
            >
              <Slide
                item={item}
                index={i}
                aspect={aspect}
                active={i === index}
                onOpenLightbox={onOpenLightbox}
              />
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="carousel__arrow carousel__arrow--prev"
        onClick={prev}
        disabled={index === 0}
        aria-label="Previous slide"
      >
        <Chevron dir="left" />
      </button>
      <button
        type="button"
        className="carousel__arrow carousel__arrow--next"
        onClick={next}
        disabled={index === count - 1}
        aria-label="Next slide"
      >
        <Chevron dir="right" />
      </button>

      {variant === 'thumbs' ? (
        <div className="carousel__thumbs" role="tablist" aria-label="Choose image">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`carousel__thumb ${i === index ? 'carousel__thumb--on' : ''}`}
              aria-selected={i === index}
              aria-label={`View ${i + 1} of ${count}`}
              aria-controls={`${baseId}-slide-${i}`}
              onClick={() => goTo(i)}
            >
              <img src={item.thumbnail_url ?? undefined} alt="" loading="lazy" />
              {item.kind === 'video' && (
                <span className="carousel__thumb-play" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="carousel__dots" role="tablist" aria-label="Choose slide">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`carousel__dot ${i === index ? 'carousel__dot--on' : ''}`}
              aria-selected={i === index}
              aria-label={`Go to slide ${i + 1} of ${count}`}
              aria-controls={`${baseId}-slide-${i}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}

      <span className="carousel__live" aria-live="polite">
        Slide {index + 1} of {count}
      </span>
    </div>
  );
}

function Slide({
  item,
  index,
  aspect,
  active,
  onOpenLightbox,
}: {
  item: Media;
  index: number;
  aspect: number;
  active?: boolean;
  onOpenLightbox?: (index: number) => void;
}) {
  const ratio = item.aspect_ratio ?? aspect;
  // Snap the slide to the image's real ratio once it loads — the stored
  // aspect_ratio is often null, so we measure naturalWidth/Height to avoid
  // letterboxing against the 16/9 fallback.
  const [naturalAr, setNaturalAr] = useState<number | null>(null);
  const ar = naturalAr ?? ratio;

  if (item.kind === 'video') {
    return (
      <div className="carousel__media carousel__media--video">
        <VideoPlayer mediaId={item.id} poster={item.thumbnail_url} aspect={ratio} />
      </div>
    );
  }

  const clickable = !!onOpenLightbox;
  return (
    <button
      type="button"
      className="carousel__media carousel__media--image"
      style={{ ['--media-ar' as string]: String(ar) } as React.CSSProperties}
      onClick={clickable ? () => onOpenLightbox?.(index) : undefined}
      disabled={!clickable}
      tabIndex={active === false ? -1 : 0}
      aria-label={clickable ? 'Open image' : undefined}
    >
      <img
        src={item.thumbnail_url ?? undefined}
        alt=""
        loading={index === 0 ? 'eager' : 'lazy'}
        onLoad={(e) => {
          const im = e.currentTarget;
          if (im.naturalWidth > 0 && im.naturalHeight > 0) {
            setNaturalAr(im.naturalWidth / im.naturalHeight);
          }
        }}
      />
      {clickable && (
        <span className="carousel__expand" aria-hidden="true">
          <ExpandIcon />
        </span>
      )}
    </button>
  );
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 3H5a2 2 0 0 0-2 2v4m18 0V5a2 2 0 0 0-2-2h-4M3 15v4a2 2 0 0 0 2 2h4m12-6v4a2 2 0 0 1-2 2h-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
